import React, { useEffect } from 'react';

interface Script {
  src?: string;
  content?: string;
  type?: string;
  async?: boolean;
  defer?: boolean;
  id?: string;
}

interface MetaTag {
  name?: string;
  property?: string;
  content: string;
  id?: string;
}

interface LinkTag {
  rel: string;
  href: string;
  type?: string;
  id?: string;
}

interface HeadManagerProps {
  title?: string;
  description?: string;
  keywords?: string;
  author?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  favicon?: string;
  scripts?: Script[];
  metaTags?: MetaTag[];
  linkTags?: LinkTag[];
  children?: React.ReactNode;
}

const HeadManager: React.FC<HeadManagerProps> = ({
  title,
  description,
  keywords,
  author,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  twitterTitle,
  twitterDescription,
  twitterImage,
  favicon,
  scripts = [],
  metaTags = [],
  linkTags = [],
  children
}) => {
  useEffect(() => {
    const head = document.head;

    // Atualizar title
    if (title) {
      document.title = title;
    }

    // Função auxiliar para criar ou atualizar elemento
    const createOrUpdateElement = (
      tagName: string,
      attributes: Record<string, string>,
      content?: string
    ) => {
      const selector = Object.entries(attributes)
        .map(([key, value]) => `[${key}="${value}"]`)
        .join('');

      let element = head.querySelector(`${tagName}${selector}`) as HTMLElement;

      if (!element) {
        element = document.createElement(tagName);
        head.appendChild(element);
      }

      // Atualizar atributos
      Object.entries(attributes).forEach(([key, value]) => {
        element.setAttribute(key, value);
      });

      // Atualizar conteúdo se for meta tag
      if (content && (tagName === 'meta' || tagName === 'script')) {
        if (tagName === 'script') {
          element.textContent = content;
        } else if (attributes.name || attributes.property) {
          element.setAttribute('content', content);
        }
      }

      return element;
    };

    // Meta tags básicas
    if (description) {
      createOrUpdateElement('meta', { name: 'description' }, description);
    }

    if (keywords) {
      createOrUpdateElement('meta', { name: 'keywords' }, keywords);
    }

    if (author) {
      createOrUpdateElement('meta', { name: 'author' }, author);
    }

    // Canonical URL
    if (canonical) {
      createOrUpdateElement('link', { rel: 'canonical', href: canonical });
    }

    // Open Graph tags
    if (ogTitle) {
      createOrUpdateElement('meta', { property: 'og:title' }, ogTitle);
    }

    if (ogDescription) {
      createOrUpdateElement('meta', { property: 'og:description' }, ogDescription);
    }

    if (ogImage) {
      createOrUpdateElement('meta', { property: 'og:image' }, ogImage);
    }

    if (ogUrl) {
      createOrUpdateElement('meta', { property: 'og:url' }, ogUrl);
    }

    if (ogType) {
      createOrUpdateElement('meta', { property: 'og:type' }, ogType);
    }

    // Twitter Card tags
    if (twitterCard) {
      createOrUpdateElement('meta', { name: 'twitter:card' }, twitterCard);
    }

    if (twitterTitle) {
      createOrUpdateElement('meta', { name: 'twitter:title' }, twitterTitle);
    }

    if (twitterDescription) {
      createOrUpdateElement('meta', { name: 'twitter:description' }, twitterDescription);
    }

    if (twitterImage) {
      createOrUpdateElement('meta', { name: 'twitter:image' }, twitterImage);
    }

    // Favicon
    if (favicon) {
      createOrUpdateElement('link', { rel: 'icon', href: favicon });
    }

    // Scripts personalizados
    scripts.forEach((script, index) => {
      const scriptId = script.id || `custom-script-${index}`;
      const existingScript = head.querySelector(`#${scriptId}`);

      if (!existingScript) {
        const scriptElement = document.createElement('script');
        scriptElement.id = scriptId;

        if (script.src) {
          scriptElement.src = script.src;
        }

        if (script.content) {
          scriptElement.textContent = script.content;
        }

        if (script.type) {
          scriptElement.type = script.type;
        }

        if (script.async) {
          scriptElement.async = true;
        }

        if (script.defer) {
          scriptElement.defer = true;
        }

        head.appendChild(scriptElement);
      }
    });

    // Meta tags personalizadas
    metaTags.forEach((meta, index) => {
      const metaId = meta.id || `custom-meta-${index}`;
      const attributes: Record<string, string> = { id: metaId };

      if (meta.name) {
        attributes.name = meta.name;
      }

      if (meta.property) {
        attributes.property = meta.property;
      }

      createOrUpdateElement('meta', attributes, meta.content);
    });

    // Link tags personalizadas
    linkTags.forEach((link, index) => {
      const linkId = link.id || `custom-link-${index}`;
      const attributes: Record<string, string> = { id: linkId, rel: link.rel, href: link.href };

      if (link.type) {
        attributes.type = link.type;
      }

      createOrUpdateElement('link', attributes);
    });

    // Cleanup function
    return () => {
      // Remover scripts personalizados quando o componente for desmontado
      scripts.forEach((script, index) => {
        const scriptId = script.id || `custom-script-${index}`;
        const scriptElement = head.querySelector(`#${scriptId}`);
        if (scriptElement) {
          head.removeChild(scriptElement);
        }
      });
    };
  }, [
    title,
    description,
    keywords,
    author,
    canonical,
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl,
    ogType,
    twitterCard,
    twitterTitle,
    twitterDescription,
    twitterImage,
    favicon,
    scripts,
    metaTags,
    linkTags
  ]);

  return <>{children}</>;
};

export default HeadManager;
